import { useTranslation } from "react-i18next";
import {
  type AwardCriterionDto,
  AwardSubCriterionAnswerFormat,
  type LibraryAwardSubCriterionDto,
  type MinimumRequirementDto,
  MinimumRequirementResponseType,
  type SelectionCriterionDto,
} from "../../autogen/bff-api";
import { tiptapToPlainText } from "../input/TipTap/tiptapToPlainText";
import { criterionTypeLabelKey } from "../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";

// t('Yes / No') t('Yes / No + comment') t('Requires documentation') t('weight {{n}} %')

/**
 * The one-line summary under a requirement's title, wherever it is listed — the section lists, the
 * library panel, the template editor.
 *
 * It lived once per list and once more in the panel, and the copies had already drifted: the panel
 * dropped the description from qualification requirements while the list kept it.
 */
export const useRequirementDescription = () => {
  const { t } = useTranslation();

  const join = (parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" · ");

  return {
    minimum: (r: MinimumRequirementDto) =>
      join([
        r.responseType === MinimumRequirementResponseType.YesNoComment
          ? t("Yes / No + comment")
          : r.responseType === MinimumRequirementResponseType.YesNoDocument
            ? t("Yes / No + document")
            : t("Yes / No"),
        tiptapToPlainText(r.description),
      ]),

    selection: (c: SelectionCriterionDto, opts: { titleShowsType?: boolean } = {}) =>
      join([
        !opts.titleShowsType && t(c.type),
        c.requiresEvidence && t("Requires documentation"),
        tiptapToPlainText(c.description),
      ]),

    /**
     * A criterion with no name is titled by its type, so repeating the type in the description would
     * say the same thing twice — the caller decides by telling us whether the title carries it.
     */
    award: (c: AwardCriterionDto, opts: { titleShowsType?: boolean } = {}) =>
      join([
        !opts.titleShowsType && t(criterionTypeLabelKey(c.type)),
        c.weight?.number != null && t("weight {{n}} %", { n: c.weight.number }),
        tiptapToPlainText(c.description),
      ]),

    /**
     * A standalone requirement. No weight: a share belongs to one criterion in one competition, and is
     * settled where the criterion is used — so the summary names the criterion type it belongs under,
     * which outside a section is the only thing placing it.
     */
    awardSub: (c: LibraryAwardSubCriterionDto, opts: { titleShowsType?: boolean } = {}) =>
      join([
        !opts.titleShowsType && t(criterionTypeLabelKey(c.criterionType)),
        // Free text is what a requirement used to be and still is by default, so naming it would add a
        // word to every line without distinguishing anything.
        c.answerFormat != null && c.answerFormat !== AwardSubCriterionAnswerFormat.FreeText && t(c.answerFormat),
        c.requiresDocumentation && t("Requires documentation"),
        tiptapToPlainText(c.description),
      ]),
  };
};
